import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
    DataState,
    DenyTrialExtensionPayload,
    ListResponse,
    ListSearch,
    LoadingState,
    RequestExtensionPayload,
    TrialExtensionRequest,
    UserLimit,
    UserLimitKind,
    UserSubscription,
} from '@ca/core/models';
import { DialogService, UserNotificationService, UserService } from '@ca/core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subject, from, iif } from 'rxjs';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';
import { AdminUser } from 'src/app/core/models/admin-user.model';
import { DeleteConfirmationDialogComponent } from 'src/app/shared/components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { PERMISSION_APPLICATIONS } from 'src/app/shared/constants/constants';
import { extendDate, getNewDate } from '../../../../../utils/date/date-utils';
import { ExtendTrialActionsDialogComponent } from '../../../admin/components/extend-trial-actions-dialog/extend-trial-actions-dialog.component';
import { ExtendTrialDialogComponent } from '../../components/extend-trial-dialog/extend-trial-dialog.component';

@Injectable()
export class SubscriptionFacadeService {
    private userLimitsState$$: BehaviorSubject<DataState<Map<UserLimitKind, UserLimit | string>>> = new BehaviorSubject({ loadingState: LoadingState.INIT });
    private usersState$$ = new BehaviorSubject<DataState<ListResponse<AdminUser>>>({ loadingState: LoadingState.INIT });
    private trialExtensionRequestsState$$ = new BehaviorSubject<DataState<ListResponse<TrialExtensionRequest>>>({ loadingState: LoadingState.INIT });
    private refresh$$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private modalService: NgbModal,
        private dialogService: DialogService,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private datePipe: DatePipe,
    ) {}

    get userLimitsState$(): Observable<DataState<Map<UserLimitKind, UserLimit | string>>> {
        return this.userLimitsState$$.asObservable();
    }

    get trialExtensionRequestsState$(): Observable<DataState<ListResponse<TrialExtensionRequest>>> {
        return this.trialExtensionRequestsState$$.asObservable();
    }

    get userState$(): Observable<DataState<ListResponse<AdminUser>>> {
        return this.usersState$$.asObservable();
    }

    get refresh$(): Observable<void> {
        return this.refresh$$.asObservable();
    }

    getUsers(options: ListSearch): void {
        this.usersState$$.next({ loadingState: LoadingState.LOADING });
        this.userService
            .searchUsers$(options)
            .pipe(
                tap(users => {
                    this.usersState$$.next({ loadingState: LoadingState.LOADED, data: users });
                }),
                take(1),
            )
            .subscribe();
    }

    onDeletionUserAction(user: AdminUser): void {
        const modalRef = this.modalService.open(DeleteConfirmationDialogComponent, { size: 'md' });
        const componentInstance = modalRef.componentInstance as DeleteConfirmationDialogComponent;
        componentInstance.title = this.translateService.instant('ADMINISTRATION.ADMIN_USERS_MANAGEMENT.DELETE_DIALOG_DELETE_USER');
        componentInstance.description = this.translateService.instant('ADMINISTRATION.ADMIN_USERS_MANAGEMENT.DELETE_DIALOG_USER_NAME', {
            firstName: user.firstName,
            lastName: user.lastName,
        });
        componentInstance.inputLabel = this.translateService.instant('ADMINISTRATION.ADMIN_USERS_MANAGEMENT.DELETE_DIALOG_ENTER_USER_NAME_TO_CONFIRM');
        componentInstance.confirmationText = user.firstName;
        from(modalRef.result)
            .pipe(
                switchMap(() => {
                    this.userNotificationService.showSpinner();
                    return this.userService.deleteUsers$(user.id);
                }),
                tap(() => this.refresh$$.next()),
                finalize(() => this.userNotificationService.hideSpinner()),
            )
            .subscribe();
    }

    getUserLimits(): void {
        this.userLimitsState$$.next({ loadingState: LoadingState.LOADING });
        this.userService
            .getUserLimits$()
            .pipe(
                tap(userLimits => {
                    this.userLimitsState$$.next({ loadingState: LoadingState.LOADED, data: userLimits });
                }),
                take(1),
            )
            .subscribe();
    }

    requestSubscriptionOffer(currentSubscription: UserSubscription, hasExpiredToday: boolean = false): void {
        const trialSubscriptionType = currentSubscription.name.toLowerCase().includes('trial');

        from(
            this.dialogService.openConfirm({
                modalTitle: this.translateService.instant(
                    hasExpiredToday
                        ? trialSubscriptionType
                            ? 'NOTIFICATIONS.TRIAL_PERIOD_ENDS_TODAY'
                            : 'NOTIFICATIONS.SUBSCRIPTION_PERIOD_ENDS_TODAY'
                        : 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.CONFIRM_UPGRADE_DIALOG.TITLE',
                ),
                message: this.translateService.instant(
                    hasExpiredToday
                        ? trialSubscriptionType
                            ? 'NOTIFICATIONS.TRIAL_PERIOD_ENDS_TODAY_MESSAGE'
                            : 'NOTIFICATIONS.SUBSCRIPTION_PERIOD_ENDS_TODAY_MESSAGE'
                        : 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.CONFIRM_UPGRADE_DIALOG.MESSAGE',
                    {
                        time: getNewDate(currentSubscription.endDate).format('HH:mm:ss'),
                        url: 'https://docs.apeer.com/about-apeer',
                    },
                ),
                cancelText: this.translateService.instant(hasExpiredToday ? 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.GOT_IT' : 'GENERAL.CANCEL'),
                confirmText: this.translateService.instant(hasExpiredToday ? 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.REQUEST_UPGRADE' : 'GENERAL.CONFIRM'),
                options: {
                    size: 'lg',
                },
            }).result,
        )
            .pipe(
                tap(() => this.userNotificationService.showSpinner()),
                switchMap(() => this.userService.requestSubscriptionOffer$()),
                tap(() => {
                    const titleKey = currentSubscription.name.toLowerCase().includes('trial')
                        ? 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.REQUEST_FOR_UPGRADE_SENT'
                        : 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.REQUEST_FOR_OFFER_SENT';
                    const title = this.translateService.instant(titleKey);
                    const messageKey = currentSubscription.name.toLowerCase().includes('trial')
                        ? 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.REQUEST_FOR_UPGRADE_SENT_MESSAGE'
                        : 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.REQUEST_FOR_OFFER_SENT_MESSAGE';
                    const message = this.translateService.instant(messageKey);
                    this.dialogService.showSuccess({ title, message });
                }),
                finalize(() => this.userNotificationService.hideSpinner()),
                take(1),
            )
            .subscribe();
    }

    cancelSubscription(subscriptionEndDate: string): void {
        const message = this.translateService.instant('MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.SURE_TO_CANCEL_SUBSCRIPTION', {
            subscriptionEndDate: this.datePipe.transform(subscriptionEndDate),
        });
        const confirmText = this.translateService.instant('MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.CANCEL_SUBSCRIPTION_CONFIRM');
        const modalRef = this.dialogService.openConfirm({ message, confirmText });
        from(modalRef.result)
            .pipe(
                switchMap((): Observable<boolean> => {
                    this.userNotificationService.showSpinner();
                    return this.userService.cancelSubscription$();
                }),
                tap((): void => {
                    const title = this.translateService.instant('MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.SUBSCRIPTION_CANCELLED_SUCCESSFULLY');
                    const message = this.translateService.instant('MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.SUBSCRIPTION_CANCELLED_SUCCESSFULLY_MESSAGE', {
                        subscriptionEndDate: this.datePipe.transform(subscriptionEndDate),
                    });
                    this.dialogService.showSuccess({ title, message });
                }),
                finalize((): void => this.userNotificationService.hideSpinner()),
                catchError((): Observable<never> => {
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe();
    }

    requestExtension(userId: string, endDate: string, period: 'Monthly' | 'Yearly' | 'Unlimited', subscriptionName: string): void {
        const hasPermission = this.userService.hasPermissions([PERMISSION_APPLICATIONS]);
        const extendTrialModal = this.modalService.open(ExtendTrialDialogComponent);
        const extendTrialModalComponent = extendTrialModal.componentInstance as ExtendTrialDialogComponent;
        extendTrialModalComponent.isBiotechUser = hasPermission;
        extendTrialModalComponent.pathToAppend = userId;
        const isAcademiaTrial = subscriptionName == 'Academia Student Trial' ? true : false;
        extendTrialModalComponent.isAcademiaTrial = isAcademiaTrial;
        extendTrialModalComponent.onSubmit
            .pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap((payload: RequestExtensionPayload): Observable<void> => this.userService.requestTrialExtension$(payload)),
                tap((): void => {
                    const newEndDate = extendDate(endDate, 1, period === 'Monthly' ? 'month' : 'year').format('DD.MM.YYYY');
                    const title = this.translateService.instant(
                        `MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.EXTEND_TRIAL_DIALOG.${!isAcademiaTrial ? 'REQUEST_SENT_TITLE' : 'TRIAL_EXTENDED_TITLE'}`,
                    );
                    const message = this.translateService.instant(
                        `MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.EXTEND_TRIAL_DIALOG.${!isAcademiaTrial ? 'REQUEST_SENT_MESSAGE' : 'TRIAL_EXTENDED_MESSAGE'}`,
                        { newEndDate },
                    );
                    const buttonAction: Function = (): void => {
                        this.userService.loadUserSubscription();
                    };
                    this.dialogService.showSuccess({
                        options: { size: 'lg' },
                        title,
                        message,
                        buttonAction,
                    });
                }),
                finalize((): void => this.userNotificationService.hideSpinner()),
                take(1),
            )
            .subscribe();
    }

    searchTrialExtensionRequests(options: ListSearch): void {
        this.trialExtensionRequestsState$$.next({ loadingState: LoadingState.LOADING });
        this.userService
            .searchTrialExtensionRequests$(options)
            .pipe(
                tap((response: ListResponse<TrialExtensionRequest>): void => {
                    this.trialExtensionRequestsState$$.next({ loadingState: LoadingState.LOADED, data: response });
                }),
                catchError((): Observable<never> => {
                    this.userNotificationService.showError('MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_GETTING_TRIAL_EXTENSION_REQUESTS');
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe();
    }

    onTrialExtensionAction({ id: trialExtensionId, ...request }: TrialExtensionRequest, action: 'approve' | 'deny'): void {
        const extendTrialActionsModal = this.modalService.open(ExtendTrialActionsDialogComponent);
        const extendTrialActionsModalComponent = extendTrialActionsModal.componentInstance as ExtendTrialActionsDialogComponent;
        extendTrialActionsModalComponent.action = action;
        extendTrialActionsModalComponent.request = request;
        extendTrialActionsModalComponent.onSubmit
            .pipe(
                tap((): void => {
                    this.userNotificationService.showSpinner();
                }),
                switchMap(
                    (payload: void | DenyTrialExtensionPayload): Observable<void> =>
                        iif(
                            (): boolean => action === 'approve',
                            this.userService.approveTrialExtension$(trialExtensionId!),
                            this.userService.denyTrialExtension$(trialExtensionId!, payload as DenyTrialExtensionPayload),
                        ),
                ),
                finalize((): void => {
                    this.userNotificationService.hideSpinner();
                    this.refresh$$.next();
                }),
                take(1),
            )
            .subscribe();
    }
}
